import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Spacing, Colors } from '@/shared/theme';
import { Button, Title, Text, CenteredColumn } from '@/shared/ui';
import { useAnalytics } from '@/hooks/useAnalytics';
import { CRISIS_RESOURCE_CLICKED } from '@/consts/events';

// Non-PHI resource identifiers for the crisis_resource_clicked event.
type CrisisResourceId = '988_call' | 'crisis_text_line';

interface CrisisResourceBlockProps {
  /** Non-blocking acknowledge — the user reads the resources and continues. */
  onContinue: () => void;
}

const Container = styled.div`
  width: 100%;
  max-width: 560px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${Spacing.x2}px;
  background-color: ${Colors.backgroundAlt};
  border-radius: ${Spacing.x1}px;
  padding: ${Spacing.x3}px;
  box-sizing: border-box;
`;

const ResourceList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${Spacing.x1_5}px;
  width: 100%;
`;

// Discrete emergency action rows (not prose links) — min-height 44px meets
// the PRD §11 tap-target minimum for 988/Crisis-Text-Line.
const ResourceLink = styled.a`
  display: flex;
  align-items: center;
  min-height: 44px;
  color: ${Colors.primary};
  font-weight: 700;
  text-decoration: none;
  &:hover,
  &:focus {
    text-decoration: underline;
  }
`;

const Footer = styled.div`
  margin-top: ${Spacing.x3}px;
  width: 100%;
  display: flex;
  justify-content: center;
`;

/**
 * RND-35692 — Inline crisis-resource block shown when PHQ-9 Q9 (suicidality) is
 * endorsed (Q9 >= 1). Non-blocking: the user acknowledges and continues.
 *
 * Pre-launch is display-only — no backend Medical Director alert fires (RND-33902
 * specifies one for V1). This behavior is pending Edo's written clinical
 * risk-acceptance and the copy is DRAFT pending clinical/legal review
 * (PRD 11 §4.2 / §9; Tech Design §6.5/§14).
 */
export const CrisisResourceBlock: React.FC<CrisisResourceBlockProps> = ({ onContinue }) => {
  const { t } = useTranslation();
  const { track } = useAnalytics();

  const handleResourceClick = (resource: CrisisResourceId) => () => {
    // resource id is not PHI — safe to emit.
    track({ event: CRISIS_RESOURCE_CLICKED, properties: { resource } });
  };

  return (
    <CenteredColumn span={8}>
      {/* WCAG 4.1.3: urgent safety message announced to assistive tech. */}
      <Container role="alert">
        <Title size="h3" centered>
          {t('crisis-resource.title')}
        </Title>
        <Text size="p1" centered>
          {t('crisis-resource.intro')}
        </Text>
        <ResourceList>
          <ResourceLink href="tel:988" onClick={handleResourceClick('988_call')}>
            {t('crisis-resource.call-988')}
          </ResourceLink>
          <ResourceLink href="sms:741741?body=HOME" onClick={handleResourceClick('crisis_text_line')}>
            {t('crisis-resource.crisis-text-line')}
          </ResourceLink>
          <Text size="p2">{t('crisis-resource.emergency')}</Text>
        </ResourceList>
      </Container>
      <Footer>
        <CenteredColumn span={6}>
          <Button text={t('crisis-resource.continue')} onClick={onContinue} variant="brand-primary" />
        </CenteredColumn>
      </Footer>
    </CenteredColumn>
  );
};
